import { RouterLink as NextLink } from "../../router";
import type { ReactNode } from "react";
import { Button } from "../../button";
import { PageHeader } from "../page-header";

/** Range presets exposed as URL-driven pills in the header. */
const PRESETS = ["7d", "30d", "90d"] as const;
export type DashboardRangePreset = (typeof PRESETS)[number];

export interface ProductDashboardHeaderProps {
  /** Product display label, e.g. "CRM". */
  label: string;
  /** One-line tagline from `marketing.tagline` or `description`. */
  tagline?: string;
  /** Base path for range navigation, e.g. "/crm". */
  basePath: string;
  /** Renders the preset pills when set; consumed via `?range=<preset>`. */
  timeScoped?: boolean;
  /** Current preset; defaults to `30d` when timeScoped is on. */
  currentRange?: DashboardRangePreset;
  /**
   * Optional override for the right-side action area. Default = a single
   * "Open {primaryActionLabel}" link pointing at `primaryActionHref`.
   */
  headerActions?: ReactNode;
  /** Label for the default primary action (e.g. "Pipeline", "Jobs"). */
  primaryActionLabel?: string;
  /** Href for the default primary action. */
  primaryActionHref?: string;
}

/**
 * The header strip at the top of a per-product dashboard. Renders through the
 * shared `<PageHeader>` so every product dashboard reads identically to every
 * other top-level page: title as the H1, tagline as the subtitle below it, and
 * the (optional) URL-driven range pills + primary action in the toolbar slot.
 * Server-component friendly — uses NextLink for nav, never `use client`.
 *
 * The range pills navigate to `${basePath}?range=<preset>`; the page
 * server component reads the search param and feeds it into
 * `resolveDashboardRange`.
 */
export function ProductDashboardHeader({
  label,
  tagline,
  basePath,
  timeScoped = false,
  currentRange = "30d",
  headerActions,
  primaryActionLabel,
  primaryActionHref,
}: ProductDashboardHeaderProps) {
  const primary =
    headerActions ??
    (primaryActionHref && primaryActionLabel ? (
      <Button variant="outline" asChild>
        <NextLink href={primaryActionHref}>Open {primaryActionLabel}</NextLink>
      </Button>
    ) : null);

  const rangePills = timeScoped ? (
    <div className="flex items-center gap-1 type-body">
      {PRESETS.map((preset) => {
        const active = preset === currentRange;
        const href = `${basePath}?range=${preset}`;
        return (
          <NextLink
            key={preset}
            href={href}
            className={[
              "inline-flex h-7 items-center rounded-md px-2.5 type-body tabular-nums transition-colors",
              active
                ? "bg-accent/40 text-foreground"
                : "text-muted-foreground hover:bg-accent/20 hover:text-foreground",
            ].join(" ")}
            aria-current={active ? "page" : undefined}
          >
            {preset}
          </NextLink>
        );
      })}
    </div>
  ) : null;

  const toolbar =
    rangePills || primary ? (
      <>
        {rangePills}
        {primary}
      </>
    ) : undefined;

  return <PageHeader title={label} subtitle={tagline} toolbar={toolbar} />;
}
